/**
 * Program Category Detail Page
 *
 * Detailed view of an individual program category with linked programs
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { programCategoryAPI, drugProgramAPI, apiHelpers } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Separator
} from '../components/ui'
import ProgramCategoryEditModal from '../components/ProgramCategoryEditModal'

export default function ProgramCategoryDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, currentClinicId } = useAuthStore()

  const [category, setCategory] = useState(null)
  const [linkedPrograms, setLinkedPrograms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  // Check if user has access
  const hasClinicAccess = user?.role === 'super_admin' || (currentClinicId && user?.clinicIds?.includes(currentClinicId))

  useEffect(() => {
    if (hasClinicAccess) {
      fetchCategoryDetails()
    }
  }, [id, hasClinicAccess])

  const fetchCategoryDetails = async () => {
    try {
      setLoading(true)
      const response = await programCategoryAPI.get(id)

      if (response.data.success) {
        const categoryData = response.data.data
        setCategory(categoryData)

        // Fetch linked programs if there are any
        if (categoryData.program_definition_ids?.length > 0) {
          await fetchLinkedPrograms(categoryData.program_definition_ids)
        }
      } else {
        throw new Error(response.data.message || 'Category not found')
      }
    } catch (err) {
      console.error('Failed to fetch category details:', err)
      setError(apiHelpers.handleError(err).message)
    } finally {
      setLoading(false)
    }
  }

  const fetchLinkedPrograms = async (programIds) => {
    try {
      // Fetch all programs and filter locally since we don't have a batch fetch endpoint
      const response = await drugProgramAPI.list({ limit: 1000 })
      if (response.data.success) {
        const allPrograms = response.data.programs || response.data.data || []
        const linked = allPrograms.filter(p =>
          programIds.includes(p._id) || programIds.includes(p.entity_id)
        )
        setLinkedPrograms(linked)
      }
    } catch (err) {
      console.error('Failed to fetch linked programs:', err)
    }
  }

  const handleCategorySave = (updatedCategory) => {
    setCategory(updatedCategory)
    fetchCategoryDetails() // Refresh to get updated data
  }

  // Format JSON for display
  const formatJson = (obj) => {
    try {
      return JSON.stringify(obj, null, 2)
    } catch {
      return String(obj)
    }
  }

  if (!hasClinicAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Clinic Selection Required</CardTitle>
            <CardDescription>Please select a clinic from the header dropdown to view category details.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading category details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error Loading Category</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/program-categories')}>
                Back to Categories
              </Button>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Category Not Found</CardTitle>
            <CardDescription>The requested program category could not be found.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate('/program-categories')}>
              Back to Categories
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <Button
            variant="outline"
            onClick={() => navigate('/program-categories')}
            className="flex items-center gap-2 mb-3 sm:mb-4"
            size="sm"
          >
            ← Back
          </Button>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-3xl font-bold text-foreground break-words">{category.display_name}</h1>
              <p className="text-sm text-muted-foreground mt-1 break-all">
                {category.category_key}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {category.allow_multiple_concurrent && (
                <Badge variant="secondary">Multi-Concurrent</Badge>
              )}
              <Button
                variant="outline"
                onClick={() => setIsEditModalOpen(true)}
                size="sm"
              >
                Edit
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Linked Programs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{category.program_definition_ids?.length || 0}</div>
              <p className="text-xs text-muted-foreground">
                Drug programs in this category
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Required Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{category.required_metrics?.length || 0}</div>
              <p className="text-xs text-muted-foreground">
                Qualification metrics
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Marketing Sections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Object.keys(category.marketing_copy || {}).length}</div>
              <p className="text-xs text-muted-foreground">
                Content sections
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Category Details */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Category Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Basic Information</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><span className="font-medium">Category Key:</span> {category.category_key}</p>
                    <p><span className="font-medium">Display Name:</span> {category.display_name}</p>
                    <p>
                      <span className="font-medium">Multiple Concurrent:</span>{' '}
                      {category.allow_multiple_concurrent ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Required Metrics Card */}
            <Card>
              <CardHeader>
                <CardTitle>Required Metrics</CardTitle>
                <CardDescription>Qualification criteria for this category</CardDescription>
              </CardHeader>
              <CardContent>
                {category.required_metrics?.length > 0 ? (
                  <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-x-auto max-h-[300px]">
                    {formatJson(category.required_metrics)}
                  </pre>
                ) : (
                  <p className="text-sm text-muted-foreground">No required metrics defined</p>
                )}
              </CardContent>
            </Card>

            {/* Marketing Copy Card */}
            <Card>
              <CardHeader>
                <CardTitle>Marketing Copy</CardTitle>
                <CardDescription>Content sections for this category</CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(category.marketing_copy || {}).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(category.marketing_copy).map(([key, section]) => (
                      <div key={key} className="border-b pb-3 last:border-0 last:pb-0">
                        <h4 className="font-medium text-sm capitalize mb-1">{key}</h4>
                        <p className="text-sm font-medium">{section.title}</p>
                        {section.description && (
                          <p className="text-xs text-muted-foreground mt-1">{section.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No marketing copy defined</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Linked Programs */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Linked Programs</CardTitle>
                <CardDescription>
                  Drug programs associated with this category
                </CardDescription>
              </CardHeader>
              <CardContent>
                {linkedPrograms.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No programs linked to this category</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      Add Programs
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {linkedPrograms.map(program => (
                      <div
                        key={program._id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">{program.drug_name}</div>
                          <div className="text-sm text-muted-foreground">
                            {program.entity_id} - {program.dosespot_type}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {program.dea && <Badge variant="destructive" className="text-xs">DEA</Badge>}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/drug-programs/${program._id}`)}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <ProgramCategoryEditModal
        category={category}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleCategorySave}
      />
    </div>
  )
}
